import * as React from 'react';
import { Button } from './Button';
import { cn } from './Input';

const SRPPaginationButton = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      className={cn(
        'font-medium rounded-lg h-8 w-8 inline-flex items-center justify-center hover:bg-zinc-100',
        className
      )}
      {...rest}
    />
  );
};

interface PaginationProps {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export const SRPPagination: React.FC<PaginationProps> = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const handlePreviousClick = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    if (totalPages <= 7) {
      // If there are 7 or fewer pages, show all pages.
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(
          <SRPPaginationButton
            key={i}
            className={i === currentPage ? 'font-bold' : ''}
            onClick={() => handlePageClick(i)}
          >
            {i}
          </SRPPaginationButton>
        );
      }
    } else {
      // Show the first page, the last page, and three pages in the middle.
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(
            <SRPPaginationButton
              key={i}
              className={i === currentPage ? 'font-bold' : ''}
              onClick={() => handlePageClick(i)}
            >
              {i}
            </SRPPaginationButton>
          );
        }
        pageNumbers.push(<div>...</div>);
        pageNumbers.push(
          <SRPPaginationButton
            key={totalPages}
            onClick={() => handlePageClick(totalPages)}
          >
            {totalPages}
          </SRPPaginationButton>
        );
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(
          <SRPPaginationButton key={1} onClick={() => handlePageClick(1)}>
            1
          </SRPPaginationButton>
        );
        pageNumbers.push(<div>...</div>);
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(
            <SRPPaginationButton
              key={i}
              className={i === currentPage ? 'font-bold' : ''}
              onClick={() => handlePageClick(i)}
            >
              {i}
            </SRPPaginationButton>
          );
        }
      } else {
        pageNumbers.push(
          <SRPPaginationButton key={1} onClick={() => handlePageClick(1)}>
            1
          </SRPPaginationButton>
        );
        pageNumbers.push(<div>...</div>);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(
            <SRPPaginationButton
              key={i}
              className={i === currentPage ? 'font-bold' : ''}
              onClick={() => handlePageClick(i)}
            >
              {i}
            </SRPPaginationButton>
          );
        }
        pageNumbers.push(<div>...</div>);
        pageNumbers.push(
          <SRPPaginationButton
            key={totalPages}
            onClick={() => handlePageClick(totalPages)}
          >
            {totalPages}
          </SRPPaginationButton>
        );
      }
    }

    return pageNumbers;
  };

  return (
    <div data-chunk="pagination" className="max-w-lg mx-auto">
      <div className="flex gap-3 items-center justify-between">
        <Button variant="secondary" onClick={handlePreviousClick}>
          Previous
        </Button>
        {renderPageNumbers()}
        <Button variant="secondary" onClick={handleNextClick}>
          Next
        </Button>
      </div>
    </div>
  );
};
